export type TileCellOffset = { px: number; py: number };

/**
 * EWW-473: normalized 0..1 spacing -> pixels, scaled against the image width -- matches iOS's own
 * save/decode formula exactly (`CanvasScreenExtensions.swift`'s `updateTileSpacing`):
 *   sizeValue = imageWidth * 0.2
 *   tileSpacing (px) = sliderValue * (sizeValue / 100)      -- encode, on iOS's 0-100 slider
 *   spacing (saved, 0..1) = tileSpacing / sizeValue          -- always reduces to sliderValue/100
 * So decoding the SAME saved `spacing` back to px on any platform is just the inverse:
 *   tileSpacing (px) = spacing * sizeValue = spacing * imageWidth * 0.2
 *
 * Previously this was a FIXED `n * 100` cap, completely decoupled from the image's own size --
 * correct-ish on a canvas near 500px wide (where `0.2 * imageWidth` happens to be near 100), but
 * increasingly wrong the further the real canvas differs from that, which is exactly why an
 * iOS/Android-authored tile template could look right in one render (canvas or thumbnail, sized
 * near that coincidental range) and visibly wrong -- extra gaps -- in another (a differently-sized
 * canvas/thumbnail, where the fixed 100px cap no longer bears any relation to `imageWidth * 0.2`).
 *
 * `scale` is still supported for export-at-another-resolution callers, applied after the
 * imageWidth-relative px value exactly as before.
 */
export function spacingPxFromNormalized(
  spacingNormalized: number,
  imageWidth: number,
  scale = 1
): number {
  const n = Math.max(0, Math.min(1, Number(spacingNormalized) || 0));
  const w = Math.max(0, Number(imageWidth) || 0);
  return n * w * 0.2 * Math.max(0, scale);
}

/**
 * Repeats a composite tile (content inset by half the spacing on all sides), like BitmapShader
 * REPEAT -- matching iOS's `createSingleTileImage` (TileView.swift) exactly:
 *   totalSize = content + tileSpacing        (spacing counted ONCE per cell)
 *   tileRect  = origin (tileSpacing/2, tileSpacing/2)
 * so the visible gap between two adjacent tiles is `spacing/2 + spacing/2 == spacing`.
 *
 * EWW-473: this previously used `content + spacing * 2` with the content inset by a FULL
 * `spacing`, making every gap `2 * spacing` -- exactly double iOS/Android, which is what made an
 * iOS/Android-authored tile template render visibly sparse ("extra spaces") on web while looking
 * correctly dense on the phone.
 */
export function buildCompositeTileCellOffsets(
  imageWidth: number,
  imageHeight: number,
  contentWidth: number,
  contentHeight: number,
  spacingPx: number,
  rotation: number,
  maxCells = 2000
): TileCellOffset[] {
  const W = Math.max(1, imageWidth);
  const H = Math.max(1, imageHeight);

  const compositeW = Math.max(1, contentWidth + spacingPx);
  const compositeH = Math.max(1, contentHeight + spacingPx);
  const inset = spacingPx / 2;

  const isRotated = rotation !== 0;

  const diag = Math.sqrt(W * W + H * H);
  const effectiveW = isRotated ? diag : W;
  const effectiveH = isRotated ? diag : H;

  const cols = Math.ceil(effectiveW / compositeW) + 4;
  const rows = Math.ceil(effectiveH / compositeH) + 4;

  const startC = isRotated ? -Math.floor(cols / 2) : -1;
  const startR = isRotated ? -Math.floor(rows / 2) : -1;

  const out: TileCellOffset[] = [];

  for (let r = startR; r < rows && out.length < maxCells; r++) {
    for (let c = startC; c < cols && out.length < maxCells; c++) {
      out.push({
        px: c * compositeW + inset,
        py: r * compositeH + inset,
      });
    }
  }

  return out;
}
